import { useCallback, useEffect, useState } from "react";
import Col from "react-bootstrap/Col";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Row from "react-bootstrap/Row";
import Tooltip from "react-bootstrap/Tooltip";
import { FaBolt, FaExclamationCircle, FaFistRaised, FaShieldAlt } from "react-icons/fa";
import styled, { css, keyframes } from "styled-components";
import './BattleLayout.css';
import SimpleMoveAnimation from './SimpleMoveAnimation';
import { resolveCutout, publicCutoutUrl } from '../../data/characterCutouts';

import { ceil, startCase } from "lodash";

/* ─────────────── Battle arena — mass-poster design system ───────────────
   Shape: ink-950 stadium with a thin amber edge at top (the arena tarmac).
   Two tall ink poster-cards flanking a big italic VS wordmark with a
   diagonal amber slash — cinema fight-poster feel.
   Stats are a dense monospace data strip below the portrait so values
   stay aligned and readable. HP bar is the largest, clearest element.
----------------------------------------------------------------------- */

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.55; }
`;

const BattleContainer = styled.div`
  position: relative;
  flex: 1 1 auto;            /* fills the column so there's no wine bleeding below */
  display: flex;
  flex-direction: column;
  justify-content: flex-start;  /* cards + moves pack from the top */
  padding: var(--space-4);
  background:
    linear-gradient(180deg, var(--color-ink-900) 0%, var(--color-ink-950) 100%);
  border: 1px solid var(--color-ink-700);
  clip-path: var(--clip-card);
  color: var(--color-cream-100);
  overflow: hidden;

  /* Amber edge — stadium tarmac */
  &::before {
    content: '';
    position: absolute;
    inset: 0 0 auto 0;
    height: 3px;
    background: var(--color-amber-500);
    z-index: 1;
  }

  /* Subtle halftone field */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      radial-gradient(circle, rgba(255, 177, 60, 0.03) 1px, transparent 1px);
    background-size: 8px 8px;
    pointer-events: none;
    z-index: 0;
  }

  > * {
    position: relative;
    z-index: 2;
  }
`;

/* ─── Turn marquee — big "TURN 04 ////" fight-card header ─── */
const TurnMarquee = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: var(--space-2);
  margin-bottom: var(--space-3);
  padding: 6px 0;
`;

const TurnLabel = styled.div`
  font-family: var(--font-display);
  font-size: clamp(32px, 4.2vw, 52px);
  font-weight: var(--weight-bold);
  font-style: italic;
  letter-spacing: -0.01em;
  line-height: 0.9;
  color: var(--color-cream-100);
  text-transform: uppercase;
  text-shadow: 0 2px 0 var(--color-ink-950), 0 0 20px rgba(8, 7, 10, 0.6);

  em {
    font-style: italic;
    color: var(--color-amber-500);
    font-weight: var(--weight-bold);
    margin-left: 8px;
  }
`;

const SlashDecor = styled.span`
  font-family: var(--font-display);
  font-size: clamp(24px, 3vw, 36px);
  font-weight: var(--weight-bold);
  font-style: italic;
  color: var(--color-amber-500);
  letter-spacing: -0.08em;
  line-height: 1;
  text-shadow: 0 0 10px rgba(255, 177, 60, 0.45);
`;

const BattleRow = styled(Row)`
  position: relative;
  align-items: stretch;
  justify-content: center;
  flex: 0 0 auto;
  margin-bottom: var(--space-4);
`;

/* ─── Fight poster VS — italic Oswald wordmark + diagonal amber slash ─── */
const VsStage = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 220px;
  width: 100%;
`;

const Slash = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 160%;
  height: 2px;
  background: var(--color-amber-500);
  transform: translate(-50%, -50%) rotate(-18deg);
  box-shadow: 0 0 12px rgba(255, 177, 60, 0.45);
  z-index: 1;
`;

const VsWordmark = styled.div`
  position: relative;
  z-index: 2;
  font-family: var(--font-display);
  font-size: 84px;
  font-weight: var(--weight-bold);
  font-style: italic;
  letter-spacing: -0.02em;
  line-height: 0.9;
  color: var(--color-amber-500);
  text-shadow:
    0 2px 0 var(--color-ink-950),
    0 0 24px rgba(255, 177, 60, 0.35);
  padding: 0 var(--space-3);
  background: var(--color-ink-950);
  clip-path: var(--clip-button);
  border: 2px solid var(--color-amber-500);

  /* Tiny "ROUND" overline — fight-card energy */
  &::before {
    content: 'VERSUS';
    position: absolute;
    top: -14px;
    left: 50%;
    transform: translateX(-50%);
    font-family: var(--font-heading);
    font-style: normal;
    font-size: 9px;
    font-weight: var(--weight-bold);
    letter-spacing: var(--tracking-widest);
    color: var(--color-amber-500);
    background: var(--color-ink-950);
    padding: 2px 8px;
  }
`;

/* ─── Poster fighter card ─── */
const FighterCard = styled.div`
  position: relative;
  width: 100%;
  max-width: 240px;
  aspect-ratio: 2 / 3;
  border: 1px solid var(--color-ink-700);
  clip-path: var(--clip-card);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: var(--shadow-lg);
  background: var(--color-ink-800);

  ${(p) =>
        p.$isUser
            ? `border-top: 3px solid var(--color-amber-500);`
            : `border-top: 3px solid var(--color-red-500);`
    }

  ${(p) =>
        p.$defeated &&
        `
    .fighter-portrait, .fighter-portrait > * {
      filter: grayscale(100%) brightness(0.4);
    }
    &::after {
      content: 'DEFEATED';
      position: absolute;
      top: 38%;
      left: 50%;
      transform: translate(-50%, -50%) rotate(-14deg);
      font-family: var(--font-display);
      font-size: 30px;
      font-weight: var(--weight-bold);
      letter-spacing: var(--tracking-wider);
      color: var(--color-red-500);
      background: rgba(8, 7, 10, 0.85);
      padding: 6px 20px;
      border: 2px solid var(--color-red-500);
      z-index: 5;
    }
  `}
`;

/**
 * Portrait region — mass-poster treatment:
 *   1. Wine-red radial backdrop (red-700 center → red-900 edges)
 *   2. Halftone dot field, very low opacity, for subtle grit
 *   3. Full-body cutout PNG anchored bottom-center so feet always land on
 *      the card floor (works for any 3:4 portrait cutout)
 *   4. Bottom-up ink gradient scrim so the name is readable
 */
const Portrait = styled.div`
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  background:
    radial-gradient(
      ellipse at 50% 35%,
      var(--color-red-700) 0%,
      var(--color-red-800) 45%,
      var(--color-red-900) 100%
    );

  /* Halftone texture field */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      radial-gradient(circle, rgba(8, 7, 10, 0.22) 1px, transparent 1px);
    background-size: 5px 5px;
    opacity: 0.5;
    pointer-events: none;
    z-index: 1;
  }

  /* Bottom-up dark scrim for name readability */
  &::after {
    content: '';
    position: absolute;
    inset: auto 0 0 0;
    height: 48%;
    background: linear-gradient(
      to top,
      var(--color-ink-950) 0%,
      rgba(8, 7, 10, 0.75) 35%,
      transparent 100%
    );
    pointer-events: none;
    z-index: 3;
  }
`;

const Cutout = styled.img`
  position: absolute;
  left: 50%;
  bottom: 0;
  height: 100%;
  max-width: 140%;
  transform: translateX(-50%);
  object-fit: contain;
  object-position: bottom center;
  z-index: 2;
`;

/* Fallback when no cutout is available — just the raw server thumbnail
   centered in the wine backdrop with a subtle vignette. */
const FallbackImg = styled.img`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 65%;
  transform: translate(-50%, -55%);
  object-fit: cover;
  aspect-ratio: 1;
  clip-path: var(--clip-button);
  z-index: 2;
  box-shadow: var(--shadow-md);
`;

const NameBlock = styled.div`
  position: absolute;
  bottom: 8px;
  left: 10px;
  right: 10px;
  z-index: 4;
  text-align: left;
`;

const Billing = styled.div`
  font-family: var(--font-heading);
  font-size: 9px;
  font-weight: var(--weight-semibold);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  color: var(--color-amber-500);
  margin-bottom: 2px;
  text-shadow: 0 1px 3px rgba(8, 7, 10, 0.9);
`;

const NamePlate = styled.div`
  font-family: var(--font-display);
  font-size: var(--size-xl);
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-tight);
  text-transform: uppercase;
  color: var(--color-cream-100);
  line-height: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-shadow: 0 2px 6px rgba(8, 7, 10, 0.95);
`;

/** When both fighters share a name, disambiguate roster vs duplicate mirror matchups. */
const NameRoleTag = styled.span`
  display: block;
  font-family: var(--font-heading);
  font-size: 9px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  color: ${(p) => (p.$mine ? 'var(--color-amber-500)' : 'var(--color-red-400)')};
  margin-top: 3px;
  text-shadow: 0 1px 3px rgba(8, 7, 10, 0.9);
`;

/* ─── Data panel — monospace stat strip below portrait ─── */
const DataPanel = styled.div`
  flex-shrink: 0;
  padding: var(--space-2) var(--space-3) var(--space-3);
  background:
    linear-gradient(180deg, var(--color-ink-900) 0%, var(--color-ink-950) 100%);
  border-top: 1px solid var(--color-ink-700);
`;

/* ─── Horizontal stat strip — ⚔ 500   ◎ 500   ⚡ 500 ─── */
const StatStrip = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
  gap: var(--space-1);
  padding: 6px 4px;
  margin-bottom: var(--space-2);
  background: rgba(8, 7, 10, 0.5);
  border-top: 1px solid var(--color-ink-700);
  border-bottom: 1px solid var(--color-ink-700);
`;

const StatCell = styled.div`
  display: inline-flex;
  align-items: baseline;
  gap: 5px;
  font-family: var(--font-mono);
  font-size: 13px;
  font-weight: var(--weight-bold);
  color: ${(p) =>
        p.$boosted ? 'var(--color-success)' :
        p.$nerfed ? 'var(--color-red-500)' :
        'var(--color-cream-100)'
    };

  svg {
    font-size: 12px;
    align-self: center;
    color: var(--color-amber-500);
    opacity: 0.9;
  }

  ${(p) => p.$boosted && `svg { color: var(--color-success); }`}
  ${(p) => p.$nerfed && `svg { color: var(--color-red-500); }`}
`;

/* Shows "base → current" with the original crossed-dim and the new value bold.
   Gives you the numeric change at a glance, not just a color hint. */
const StatBase = styled.span`
  font-size: 10px;
  font-weight: var(--weight-semibold);
  color: var(--color-cream-400);
  text-decoration: line-through;
  text-decoration-color: var(--color-cream-500);
  opacity: 0.75;
`;

const StatArrow = styled.span`
  font-size: 10px;
  color: var(--color-cream-400);
  opacity: 0.75;
`;

const StatCurrent = styled.span`
  font-weight: var(--weight-bold);
`;

/* HP bar — the big primary readable element */
const HpBlock = styled.div`
  margin-top: var(--space-1);
`;

const HpTopLine = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 3px;
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--color-cream-300);
  letter-spacing: 0.04em;

  strong {
    font-size: 13px;
    color: var(--color-cream-100);
    font-weight: var(--weight-bold);
  }
`;

/* ─── Segmented HP bar — 10 discrete blocks, fight-game feel ─── */
const HP_SEGMENTS = 10;

const HpSegTrack = styled.div`
  display: grid;
  grid-template-columns: repeat(${HP_SEGMENTS}, 1fr);
  gap: 2px;
  padding: 3px;
  background: rgba(8, 7, 10, 0.75);
  border: 1px solid var(--color-ink-700);
`;

const HpSeg = styled.div`
  height: 12px;
  background: ${(p) => (p.$filled ? p.$color : 'rgba(255, 255, 255, 0.06)')};
  transition: background 0.3s var(--ease-out);
  ${(p) => p.$filled && p.$low && css`animation: ${pulse} 1.2s infinite;`}
  ${(p) => p.$filled && 'box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.18);'}
`;

const HpPct = styled.span`
  font-family: var(--font-mono);
  font-size: 13px;
  font-weight: var(--weight-bold);
  color: ${(p) => p.$color};
  letter-spacing: 0.02em;
`;

/* ─── Moves picker — sits directly below the fighter cards ─── */
const MovesContainer = styled.div`
  flex-shrink: 0;
  margin-top: var(--space-3);
  padding: var(--space-3);
  background: rgba(8, 7, 10, 0.45);
  border: 1px solid var(--color-ink-700);
  clip-path: var(--clip-card);
`;

const MovesHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: var(--space-2);
  margin-bottom: var(--space-3);
  padding-left: 4px;
  font-family: var(--font-heading);
  font-size: var(--size-xs);
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  color: var(--color-cream-100);

  /* Thin amber bar under the label, full width */
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--color-amber-500);
    opacity: 0.5;
  }

  span.slash {
    color: var(--color-amber-500);
    font-family: var(--font-display);
    font-style: italic;
    letter-spacing: -0.08em;
    margin-left: 2px;
  }
`;

const CATEGORY_STYLE = {
    attack: {
        bg: 'var(--color-red-500)',
        fg: 'var(--color-cream-100)',
        accent: 'var(--color-red-400)',
    },
    heavy: {
        bg: 'var(--color-red-400)',
        fg: 'var(--color-cream-100)',
        accent: 'var(--color-red-300)',
    },
    buff: {
        bg: 'var(--color-amber-500)',
        fg: 'var(--color-ink-950)',
        accent: 'var(--color-amber-400)',
    },
    debuff: {
        bg: 'var(--color-ink-600)',
        fg: 'var(--color-cream-100)',
        accent: 'var(--color-red-500)',
    },
    neutral: {
        bg: 'var(--color-ink-700)',
        fg: 'var(--color-cream-100)',
        accent: 'var(--color-amber-500)',
    },
};

/* Human-readable category label shown at the bottom-left of each move tile */
const CATEGORY_LABEL = {
    attack: 'Attack',
    heavy: 'Heavy Hit',
    buff: 'Buff',
    debuff: 'Debuff',
    neutral: 'Status',
};

const MoveTile = styled.button`
  all: unset;
  box-sizing: border-box;
  position: relative;
  width: 100%;
  min-height: 88px;
  padding: 10px 12px 8px 16px;    /* extra left to clear the accent stripe */
  background: ${(p) => CATEGORY_STYLE[p.$category].bg};
  color: ${(p) => CATEGORY_STYLE[p.$category].fg};
  clip-path: var(--clip-button);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 4px;
  overflow: hidden;
  transition: transform var(--dur-fast) var(--ease-out),
              filter var(--dur-fast) var(--ease-out);

  /* Left accent stripe — quick visual cue to category */
  &::before {
    content: '';
    position: absolute;
    top: 0; bottom: 0; left: 0;
    width: 4px;
    background: ${(p) => CATEGORY_STYLE[p.$category].accent};
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: brightness(1.1);
  }

  &:focus-visible {
    outline: 2px solid var(--color-amber-500);
    outline-offset: 2px;
  }

  &:disabled,
  &.character-defeated {
    opacity: 0.45;
    cursor: not-allowed;
    filter: grayscale(40%);
  }

  &:disabled::after {
    content: '';
    position: absolute;
    inset: 0;
    background: repeating-linear-gradient(
      45deg,
      rgba(8, 7, 10, 0.15),
      rgba(8, 7, 10, 0.15) 6px,
      transparent 6px,
      transparent 12px
    );
    pointer-events: none;
  }
`;

/* Move tile has 3 zones:
     Row 1: Name (gets the full width)
     Row 2: Mono stats — PWR and ACC
     Row 3: category tag left, PP pill right
   Every piece of info is always visible — no ellipsis, no collision. */
const MoveName = styled.span`
  font-family: var(--font-heading);
  font-size: 14px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-wide);
  text-transform: uppercase;
  line-height: 1.1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MoveStats = styled.span`
  display: flex;
  align-items: baseline;
  gap: 10px;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: var(--weight-semibold);
  letter-spacing: 0.02em;
  opacity: 0.92;
`;

const MoveStat = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  b {
    opacity: 0.65;
    font-weight: var(--weight-semibold);
    font-size: 9px;
    letter-spacing: 0.06em;
  }
`;

const MoveFooter = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 2px;
`;

const MoveCategoryTag = styled.span`
  font-family: var(--font-heading);
  font-size: 9px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  opacity: 0.85;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &::before {
    content: '';
    display: inline-block;
    width: 4px;
    height: 4px;
    background: currentColor;
  }
`;

/* Remaining uses — "Uses 3/5" pill at bottom-right. Escalates color. */
const UsesBadge = styled.span`
  flex: 0 0 auto;
  padding: 1px 6px;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: var(--weight-bold);
  letter-spacing: 0.04em;
  color: currentColor;
  background: rgba(8, 7, 10, 0.55);
  border: 1px solid currentColor;
  opacity: ${(p) => (p.$low || p.$empty ? 1 : 0.85)};

  small {
    opacity: 0.65;
    margin-right: 3px;
  }

  strong {
    font-size: 11px;
    color: ${(p) =>
        p.$empty ? 'var(--color-red-500)' :
        p.$low ? 'var(--color-amber-400)' :
        'inherit'};
  }
`;

const StyledTooltip = styled(Tooltip)`
  .tooltip-inner {
    max-width: 280px;
    padding: 10px 12px;
    background: var(--color-ink-900);
    border: 1px solid var(--color-amber-500);
    color: var(--color-cream-100);
    font-family: var(--font-body);
    font-size: 12px;
    text-align: left;
    border-radius: 0;
    clip-path: var(--clip-card);
  }

  .tooltip-arrow::before {
    border-bottom-color: var(--color-amber-500);
  }

  .move-stat {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
    padding-bottom: 8px;
    border-bottom: 1px solid var(--color-ink-700);
    font-family: var(--font-mono);
    font-size: 11px;

    b {
      color: var(--color-amber-500);
      font-weight: var(--weight-bold);
    }
  }

  .move-stat-icon {
    color: var(--color-amber-500);
  }

  .move-effects-title {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 6px;
    font-family: var(--font-heading);
    font-size: 11px;
    font-weight: var(--weight-bold);
    letter-spacing: var(--tracking-wider);
    text-transform: uppercase;
    color: var(--color-amber-500);
  }

  .move-effects-title::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--color-ink-700);
    margin-left: 5px;
  }

  .move-effects-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4px;
  }

  .move-effect-item {
    display: flex;
    gap: 4px;
    align-items: center;
    font-size: 11px;
    color: var(--color-cream-200);
  }

  .move-effect-icon {
    color: var(--color-success);
  }

  .move-effect-icon.negative {
    color: var(--color-red-500);
  }

  .move-uses {
    text-align: center;
    margin-top: 8px;
    padding-top: 6px;
    border-top: 1px solid var(--color-ink-700);
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--color-cream-300);
  }
`;

/* Battle notification message component */
const BattleNotification = ({ message, onClose }) => {
    useEffect(() => {
        const timer = setTimeout(() => {
            onClose();
        }, 3000);

        return () => clearTimeout(timer);
    }, [onClose]);

    return (
        <div className="battle-log-message">
            <div dangerouslySetInnerHTML={{ __html: message }} />
        </div>
    );
};

/* Categorize a move for display — one of 5 visual buckets.
   "neutral" is the safe fallback for mixed or status moves. */
const categorizeMove = (move) => {
    if (move.power > 100) return 'heavy';
    if (move.power > 0) return 'attack';

    const hasBuff = move.effects?.some((e) =>
        (e.type === 'user_stat_change' && e.change === 'increase') ||
        (e.type === 'opponent_stat_change' && e.change === 'decrease')
    );
    const hasDebuff = move.effects?.some((e) =>
        (e.type === 'user_stat_change' && e.change === 'decrease') ||
        (e.type === 'opponent_stat_change' && e.change === 'increase')
    );

    if (hasBuff && !hasDebuff) return 'buff';
    if (hasDebuff && !hasBuff) return 'debuff';
    return 'neutral';
};

const BattleLayout = (props) => {
    const [executingMove, setExecutingMove] = useState(false);
    const [notification, setNotification] = useState(null);
    const [isCharacterDefeated, setIsCharacterDefeated] = useState(false);

    useEffect(() => {
        setIsCharacterDefeated(Boolean(props.user && props.user.modifiedHealth <= 0));
    }, [props.user]);

    const showNotification = useCallback((message) => setNotification(message), []);
    const hideNotification = useCallback(() => setNotification(null), []);

    const handleCharacterDefeated = useCallback(() => {
        const message = `<span class="character-name">${props.user.name}</span> is unable to battle!<br>Select another character!`;
        showNotification(message);

        const needsSwitchEvent = new CustomEvent('needsCharacterSwitch', {
            detail: { characterName: props.user.name },
        });
        document.dispatchEvent(needsSwitchEvent);
    }, [props.user, showNotification]);

    const handleMoveClick = useCallback(
        (move) => {
            if (isCharacterDefeated) {
                handleCharacterDefeated();
                return;
            }
            props.onClickMove(move);
        },
        [isCharacterDefeated, handleCharacterDefeated, props]
    );

    useEffect(() => {
        if (props.battleAnimationData && props.battleAnimationData.actions) {
            setExecutingMove(true);
        }
    }, [props.battleAnimationData]);

    const handleMoveAnimationComplete = useCallback(() => {
        props.onMoveAnimationSequenceDone?.();
        setExecutingMove(false);
        if (props.user && props.user.modifiedHealth <= 0) {
            handleCharacterDefeated();
        }
    }, [props, handleCharacterDefeated]);

    const FighterPoster = useCallback((charecter, isUser, sameNameAsOpponent) => {
        if (!charecter) return null;

        const stats = {
            ATK: {
                current: charecter?.temporaryChanges?.attack || charecter.attack,
                original: charecter.attack,
                multiplier: charecter?.temporaryChanges?.attack
                    ? Math.round((charecter.temporaryChanges.attack / charecter.attack) * 100) / 100
                    : null,
            },
            DEF: {
                current: charecter?.temporaryChanges?.defense || charecter.defense,
                original: charecter.defense,
                multiplier: charecter?.temporaryChanges?.defense
                    ? Math.round((charecter.temporaryChanges.defense / charecter.defense) * 100) / 100
                    : null,
            },
            SPD: {
                current: charecter?.temporaryChanges?.speed || charecter.speed,
                original: charecter.speed,
                multiplier: charecter?.temporaryChanges?.speed
                    ? Math.round((charecter.temporaryChanges.speed / charecter.speed) * 100) / 100
                    : null,
            },
        };

        const currentHp = charecter.modifiedHealth ?? charecter.health;
        const maxHp = charecter.health;
        const healthPct = currentHp > 0 ? ceil((currentHp / maxHp) * 100) : 0;
        const hpColor =
            healthPct > 60 ? '#67C97A'
                : healthPct > 30 ? '#FFB13C'
                    : '#C1272D';
        const isDefeated = currentHp <= 0;
        const cutout = resolveCutout(charecter);
        const displayName = cutout?.name || charecter.name;

        return (
            <FighterCard
                $isUser={isUser}
                $defeated={isDefeated}
                className={isDefeated ? 'defeated' : ''}
            >
                <Portrait className="fighter-portrait">
                    {cutout ? (
                        <Cutout src={publicCutoutUrl(cutout.cutoutUrl)} alt={displayName} />
                    ) : (
                        <FallbackImg src={charecter.image} alt={displayName} />
                    )}
                    <NameBlock>
                        {cutout?.billing && <Billing>{cutout.billing}</Billing>}
                        <NamePlate>{displayName}</NamePlate>
                        {sameNameAsOpponent && (
                            <NameRoleTag $mine={isUser}>{isUser ? 'Yours' : 'Theirs'}</NameRoleTag>
                        )}
                    </NameBlock>
                </Portrait>

                <DataPanel>
                    <HpBlock>
                        <HpTopLine>
                            <span>HP</span>
                            <HpPct $color={hpColor}>{healthPct}%</HpPct>
                        </HpTopLine>
                        <HpSegTrack>
                            {Array.from({ length: HP_SEGMENTS }).map((_, i) => {
                                const threshold = ((i + 1) / HP_SEGMENTS) * 100;
                                const filled = healthPct >= threshold - (100 / HP_SEGMENTS) + 1;
                                return (
                                    <HpSeg
                                        key={i}
                                        $filled={filled}
                                        $color={hpColor}
                                        $low={healthPct < 20}
                                    />
                                );
                            })}
                        </HpSegTrack>
                    </HpBlock>

                    <StatStrip>
                        {Object.entries(stats).map(([label, data]) => {
                            const boosted = data.multiplier && data.multiplier > 1;
                            const nerfed = data.multiplier && data.multiplier < 1;
                            const modified = boosted || nerfed;
                            const icon =
                                label === 'ATK' ? <FaFistRaised /> :
                                label === 'DEF' ? <FaShieldAlt /> :
                                <FaBolt />;
                            return (
                                <StatCell
                                    key={label}
                                    $boosted={boosted}
                                    $nerfed={nerfed}
                                    title={
                                        modified
                                            ? `${label}: ${data.original} → ${data.current} (${data.multiplier}×)`
                                            : `${label}: ${data.current}`
                                    }
                                >
                                    {icon}
                                    {modified ? (
                                        <>
                                            <StatBase>{data.original}</StatBase>
                                            <StatArrow>{boosted ? '▲' : '▼'}</StatArrow>
                                            <StatCurrent>{data.current}</StatCurrent>
                                        </>
                                    ) : (
                                        <StatCurrent>{data.current}</StatCurrent>
                                    )}
                                </StatCell>
                            );
                        })}
                    </StatStrip>
                </DataPanel>
            </FighterCard>
        );
    }, []);

    const duplicateBattleName = Boolean(
        props.user?.name && props.opp?.name && props.user.name === props.opp.name
    );

    const waitingForOpponentSwitch = props.waitingForOpponentSwitch;
    const gameEnded = props.gameEnded;

    // Current turn = played turns + 1 (the one being decided now).
    // battleLogs is an object keyed by turn (e.g. { turn1: [...], turn2: [...] })
    // or sometimes an array. Handle both shapes defensively.
    const battleLogs = props.battleLogs;
    const playedTurns = battleLogs
        ? (Array.isArray(battleLogs) ? battleLogs.length : Object.keys(battleLogs).length)
        : 0;
    const turnNumber = playedTurns + 1;

    const MovesLayout = useCallback(
        (moves) => {
            return (
                <MovesContainer>
                    <MovesHeader>
                        Special Moves{' '}
                        <span className="slash">{'////'}</span>
                    </MovesHeader>
                    <Row>
                        {moves.map((move, index) => {
                            const disableMove =
                                move.remaining === 0 ||
                                isCharacterDefeated ||
                                waitingForOpponentSwitch ||
                                gameEnded;
                            const category = categorizeMove(move);
                            const remaining = move.remaining ?? move.limit;
                            const categoryLabel = CATEGORY_LABEL[category];
                            const isStatus = !(move.power > 0);

                            const lowUses = remaining > 0 && remaining <= 2;
                            const noUses = remaining === 0;

                            return (
                                <Col xs={6} md={3} key={index} className="mb-2">
                                    <OverlayTrigger
                                        placement="bottom"
                                        delay={{ show: 250, hide: 300 }}
                                        overlay={
                                            <StyledTooltip id={`tooltip-${move.moveId}`}>
                                                <div>
                                                    <div className="move-stat">
                                                        <span className="move-stat-icon">⚡</span>
                                                        {move.power > 0 ? (
                                                            <span>
                                                                Power: <b>{move.power}</b> | Accuracy:{' '}
                                                                <b>{move.accuracy}%</b>
                                                            </span>
                                                        ) : (
                                                            <span>
                                                                Status move | Accuracy: <b>{move.accuracy}%</b>
                                                            </span>
                                                        )}
                                                    </div>

                                                    {move.effects && move.effects.length > 0 && (
                                                        <div className="move-effects">
                                                            <div className="move-effects-title">
                                                                <FaExclamationCircle size={10} /> Effects
                                                            </div>
                                                            <div className="move-effects-grid">
                                                                {move.effects.map((effect, idx) => {
                                                                    let icon = '✨';
                                                                    let isNegative = false;
                                                                    let description = '';

                                                                    if (effect.type === 'user_stat_change') {
                                                                        const direction =
                                                                            effect.change === 'increase'
                                                                                ? 'Increases'
                                                                                : 'Decreases';
                                                                        const valueText =
                                                                            typeof effect.value === 'string'
                                                                                ? effect.value
                                                                                : `by ${effect.value}`;
                                                                        description = `${direction} your ${startCase(
                                                                            effect.field
                                                                        )} ${valueText}`;
                                                                        if (effect.change === 'increase') {
                                                                            icon = '↑';
                                                                        } else {
                                                                            icon = '↓';
                                                                            isNegative = true;
                                                                        }
                                                                    } else if (
                                                                        effect.type === 'opponent_stat_change'
                                                                    ) {
                                                                        const direction =
                                                                            effect.change === 'increase'
                                                                                ? 'Increases'
                                                                                : 'Decreases';
                                                                        const valueText =
                                                                            typeof effect.value === 'string'
                                                                                ? effect.value
                                                                                : `by ${effect.value}`;
                                                                        description = `${direction} opponent's ${startCase(
                                                                            effect.field
                                                                        )} ${valueText}`;
                                                                        if (effect.change === 'decrease') {
                                                                            icon = '↓';
                                                                        } else {
                                                                            icon = '↑';
                                                                            isNegative = true;
                                                                        }
                                                                    } else if (
                                                                        effect.type === 'special' &&
                                                                        effect.effect === 'sleep'
                                                                    ) {
                                                                        description = `You fall asleep for ${effect.turns} turns`;
                                                                        icon = '💤';
                                                                        isNegative = true;
                                                                    } else if (effect.type === 'move_repeat') {
                                                                        description = `Hits ${effect.repeat} times in succession`;
                                                                        icon = '🔄';
                                                                    } else if (effect.type === 'move_change') {
                                                                        description = `${startCase(
                                                                            effect.change
                                                                        )}es ${startCase(effect.field)} to ${effect.value}`;
                                                                        icon = '⚙️';
                                                                    }

                                                                    return (
                                                                        <div className="move-effect-item" key={idx}>
                                                                            <span
                                                                                className={`move-effect-icon ${isNegative ? 'negative' : ''
                                                                                    }`}
                                                                            >
                                                                                {icon}
                                                                            </span>
                                                                            <span>{description}</span>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    )}

                                                    <div className="move-uses">
                                                        Uses left: {remaining} / {move.limit}
                                                    </div>
                                                </div>
                                            </StyledTooltip>
                                        }
                                    >
                                        <div data-remaining={remaining}>
                                            <MoveTile
                                                disabled={disableMove}
                                                onClick={() => handleMoveClick(move)}
                                                $category={category}
                                                className={`move-button ${isCharacterDefeated ? 'character-defeated' : ''
                                                    }`}
                                                type="button"
                                            >
                                                <MoveName>{startCase(move.name)}</MoveName>
                                                <MoveStats>
                                                    {isStatus ? (
                                                        <MoveStat>
                                                            <b>TYPE</b>Status
                                                        </MoveStat>
                                                    ) : (
                                                        <MoveStat>
                                                            <b>PWR</b>{move.power}
                                                        </MoveStat>
                                                    )}
                                                    <MoveStat>
                                                        <b>ACC</b>{move.accuracy}%
                                                    </MoveStat>
                                                </MoveStats>
                                                <MoveFooter>
                                                    <MoveCategoryTag>{categoryLabel}</MoveCategoryTag>
                                                    <UsesBadge
                                                        $low={lowUses}
                                                        $empty={noUses}
                                                        title={`${remaining} uses remaining out of ${move.limit} (each use is spent even on a miss)`}
                                                    >
                                                        <small>Uses</small>
                                                        <strong>{remaining}</strong>/{move.limit}
                                                    </UsesBadge>
                                                </MoveFooter>
                                            </MoveTile>
                                        </div>
                                    </OverlayTrigger>
                                </Col>
                            );
                        })}
                    </Row>
                </MovesContainer>
            );
        },
        [handleMoveClick, isCharacterDefeated, waitingForOpponentSwitch, gameEnded]
    );

    return (
        <div className="game-container">
            {notification && (
                <BattleNotification
                    message={notification}
                    onClose={hideNotification}
                />
            )}

            {executingMove && props.battleAnimationData && (
                <SimpleMoveAnimation
                    battleAnimationData={props.battleAnimationData}
                    onAnimationComplete={handleMoveAnimationComplete}
                    viewerSocketId={props.viewerSocketId}
                    yourTrainerName={props.yourTrainerName}
                    opponentTrainerName={props.opponentTrainerName}
                />
            )}

            <BattleContainer className="battle-arena battle-container">
                {waitingForOpponentSwitch && (
                    <div className="waiting-for-opponent-overlay">
                        <div className="waiting-indicator">
                            <div className="waiting-spinner"></div>
                            <div className="waiting-text">
                                <div className="waiting-title">
                                    Opponent's character was defeated
                                </div>
                                <div className="waiting-subtitle">
                                    Waiting for them to send out their next character...
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <TurnMarquee>
                    <TurnLabel>
                        Turn <em>{String(turnNumber).padStart(2, '0')}</em>
                    </TurnLabel>
                    <SlashDecor>{'////'}</SlashDecor>
                </TurnMarquee>

                <BattleRow>
                    <Col
                        xs="12"
                        md="5"
                        className="d-flex justify-content-center justify-content-md-end"
                    >
                        {FighterPoster(props.user, true, duplicateBattleName)}
                    </Col>
                    <Col
                        xs="12"
                        md="2"
                        className="d-flex align-items-center justify-content-center"
                    >
                        <VsStage>
                            <Slash />
                            <VsWordmark>VS</VsWordmark>
                        </VsStage>
                    </Col>
                    <Col
                        xs="12"
                        md="5"
                        className="d-flex justify-content-center justify-content-md-start"
                    >
                        {FighterPoster(props.opp, false, duplicateBattleName)}
                    </Col>
                </BattleRow>

                {props.user &&
                    props.user.joinedMoves &&
                    MovesLayout(props.user.joinedMoves)}
            </BattleContainer>
        </div>
    );
};

export default BattleLayout;
